/**
 * MYAC-904 v2 — front mirror of the registry vocabulary carried by v2 report
 * entries (core_models_services `models/registry.py`).
 *
 * Historical note: this file used to be report-only because Firestore rules
 * for `registry_families` weren't deployed. That changed 2026-07-29 (rules +
 * indexes deployed: `registry_families` readable when `exposable == true`) —
 * P4.2 (MYAC-904) adds a direct, batched `registry_families` read layer (see
 * `src/stores/registryFamilies.ts`) for the norms-table chip/enriched-column
 * work (spec `work/2026-07-27-registry-identity-simplification/spec.md`
 * §7.1/§8/§9). This file stays the single source of truth for the shared
 * registry vocabulary; the family-projection types added below are the
 * client-visible subset of `RegistryFamily`, never a full mirror of the
 * server-only workflow fields (`registry_families_state`, `internal_*`).
 *
 * All string-enum-like unions follow the backend's StrEnum `_missing_`
 * resilience rule: an unrecognized value parses to `'unknown'` instead of
 * throwing, so a report (or a family doc) written by a newer backend never
 * crashes an older front build.
 */

/** Provenance of a `RegistryLink.revision_key` pin (MYAC-904 P4.3 D1) --
 *  server-written, never client-set. `typed` = parsed from the user's
 *  designation; `default` = the resolver pinned the family's
 *  `latest_revision` as a snapshot (an HONEST assumption, not a confirmed
 *  pick); `user` = picked via the edition picker (`resolver_hint`). Mirrors
 *  CMS `RevisionSource` StrEnum, `_missing_` -> `unknown`. Absent (`undefined`)
 *  on links written before this field existed -- distinct from `'unknown'`
 *  (present but unrecognized), though both degrade the same way in display
 *  logic (never treated as `'default'`). */
export type RevisionSource = 'typed' | 'default' | 'user' | 'unknown'
const REVISION_SOURCES: ReadonlySet<string> = new Set(['typed', 'default', 'user'])
export function toRevisionSource(v: unknown): RevisionSource | undefined {
  if (v === null || v === undefined) return undefined
  return typeof v === 'string' && REVISION_SOURCES.has(v) ? (v as RevisionSource) : 'unknown'
}

/** A link to a (family, revision) pair -- never free text. Structurally
 *  identical to `RegulationRegistryRef` in `RegulationListItem.ts`; kept as
 *  its own type here so the v2 report models don't couple to the
 *  regulation_lists row model. */
export interface RegistryLink {
  family_id: string
  revision_key: string | null
  /** MYAC-904 P4.3 D1 -- see `RevisionSource`. */
  revision_source?: RevisionSource
}

export type ConfidenceTier = 'curated' | 'best_effort' | 'unverified' | 'unknown'
const CONFIDENCE_TIERS: ReadonlySet<string> = new Set(['curated', 'best_effort', 'unverified'])
export function toConfidenceTier(v: unknown): ConfidenceTier {
  return typeof v === 'string' && CONFIDENCE_TIERS.has(v) ? (v as ConfidenceTier) : 'unknown'
}

export type RevisionStatus = 'in_force' | 'superseded' | 'withdrawn' | 'draft' | 'unknown'

/** What a revision's `successor` link means (backend `SuccessorRelation`).
 *  Absent/unknown is read as `supersedes` by every consumer (never
 *  under-warn); `adopts` is the one relation that does NOT replace the text. */
export type SuccessorRelation = 'supersedes' | 'consolidates' | 'adopts' | 'unknown'
const SUCCESSOR_RELATIONS: ReadonlySet<string> = new Set(['supersedes', 'consolidates', 'adopts'])
export function toSuccessorRelation(v: unknown): SuccessorRelation {
  return typeof v === 'string' && SUCCESSOR_RELATIONS.has(v) ? (v as SuccessorRelation) : 'unknown'
}
const REVISION_STATUSES: ReadonlySet<string> = new Set(['in_force', 'superseded', 'withdrawn', 'draft'])
export function toRevisionStatus(v: unknown): RevisionStatus {
  return typeof v === 'string' && REVISION_STATUSES.has(v) ? (v as RevisionStatus) : 'unknown'
}

/** Display ranking for source links: the issuing authority's own page first,
 *  then a catalog/reseller entry, then secondary info. */
export type LinkKind = 'authority' | 'catalog' | 'info' | 'unknown'
const LINK_KINDS: ReadonlySet<string> = new Set(['authority', 'catalog', 'info'])
export function toLinkKind(v: unknown): LinkKind {
  return typeof v === 'string' && LINK_KINDS.has(v) ? (v as LinkKind) : 'unknown'
}
/** Lower = shown first. Mirrors `LinkKind`'s display-ranking doc in registry.py. */
export const LINK_KIND_RANK: Record<LinkKind, number> = {
  authority: 0,
  catalog: 1,
  info: 2,
  unknown: 3,
}

export interface SourceLink {
  url: string
  label: string | null
  kind: LinkKind
}

export function parseSourceLink(raw: unknown): SourceLink | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  if (typeof r.url !== 'string' || !r.url) return null
  return {
    url: r.url,
    label: typeof r.label === 'string' ? r.label : null,
    kind: toLinkKind(r.kind),
  }
}

/** The four dated facts of a revision -- the keys of `RevisionDates` and of
 *  its `precision` map. */
export type RevisionDateField = 'published' | 'in_force' | 'transition_end' | 'expected'
export const REVISION_DATE_FIELDS: readonly RevisionDateField[] = ['published', 'in_force', 'transition_end', 'expected']

/** How much of a stored revision date was actually READ on the source (D75,
 *  mirrors CMS `DatePrecision`): `month` = only the month is known, stored as
 *  YYYY-MM-01; `year` = only the year, stored as YYYY-01-01. The day of such a
 *  date was never read and must never be shown. `_missing_` -> `unknown`,
 *  which displays like `day` (the format every date had before D75). */
export type DatePrecision = 'day' | 'month' | 'year' | 'unknown'
const DATE_PRECISIONS: ReadonlySet<string> = new Set(['day', 'month', 'year'])
export function toDatePrecision(v: unknown): DatePrecision {
  return typeof v === 'string' && DATE_PRECISIONS.has(v) ? (v as DatePrecision) : 'unknown'
}

/** All optional; absent = unknown (never inferred). */
export interface RevisionDates {
  published: Date | null
  in_force: Date | null
  transition_end: Date | null
  /** A DRAFT revision's expected publication date. */
  expected: Date | null
  /** Per date field, how precise the stored date is (D75). Absent map or key
   *  = `day`: every revision written before D75 keeps its meaning. Optional in
   *  fixtures; the parser always sets it, with only the keys present on the
   *  doc. Read it through `datePrecisionOf`, never directly. */
  precision?: Partial<Record<RevisionDateField, DatePrecision>>
}

/** The precision of one date of a revision; `day` when the doc says nothing. */
export function datePrecisionOf(dates: RevisionDates | null | undefined, field: RevisionDateField): DatePrecision {
  return dates?.precision?.[field] ?? 'day'
}

/** Finest first: a field whose day WAS read wins over a coarse one on the
 *  same calendar day. */
const PRECISION_RANK: Record<DatePrecision, number> = { day: 0, unknown: 0, month: 1, year: 2 }

/**
 * The precision of a date COPIED from one of this revision's own dates (a
 * report change, the report card's `parsed_date`): the precision of the field
 * it equals, same UTC calendar day. When several fields match, the finest wins
 * (one of them really carries that day); when none does, `day` -- the date is
 * not this revision's, and nothing says it is coarse.
 */
export function precisionOfRevisionDate(dates: RevisionDates | null | undefined, date: Date): DatePrecision {
  if (!dates || Number.isNaN(date.getTime())) return 'day'
  const day = date.toISOString().slice(0, 10)
  let best: DatePrecision | null = null
  for (const field of REVISION_DATE_FIELDS) {
    const value = dates[field]
    if (!value || value.toISOString().slice(0, 10) !== day) continue
    const precision = datePrecisionOf(dates, field)
    if (best === null || PRECISION_RANK[precision] < PRECISION_RANK[best]) best = precision
  }
  return best ?? 'day'
}

function parseDatePrecisionMap(raw: unknown): Partial<Record<RevisionDateField, DatePrecision>> {
  if (!raw || typeof raw !== 'object') return {}
  const r = raw as Record<string, unknown>
  const out: Partial<Record<RevisionDateField, DatePrecision>> = {}
  for (const field of REVISION_DATE_FIELDS) {
    if (r[field] !== undefined && r[field] !== null) out[field] = toDatePrecision(r[field])
  }
  return out
}

function toDateOrNull(v: unknown): Date | null {
  if (v === null || v === undefined) return null
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v
  const withToDate = v as { toDate?: () => Date }
  if (typeof withToDate.toDate === 'function') {
    const d = withToDate.toDate()
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null
  }
  const withSeconds = v as { seconds?: number }
  if (typeof withSeconds.seconds === 'number') {
    const d = new Date(withSeconds.seconds * 1000)
    return Number.isNaN(d.getTime()) ? null : d
  }
  if (typeof v === 'string' || typeof v === 'number') {
    const d = new Date(v)
    return Number.isNaN(d.getTime()) ? null : d
  }
  return null
}

export function parseRevisionDates(raw: unknown): RevisionDates {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  return {
    published: toDateOrNull(r.published),
    in_force: toDateOrNull(r.in_force),
    transition_end: toDateOrNull(r.transition_end),
    expected: toDateOrNull(r.expected),
    precision: parseDatePrecisionMap(r.precision),
  }
}

export function parseRegistryLink(raw: unknown): RegistryLink | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  if (typeof r.family_id !== 'string' || !r.family_id) return null
  return {
    family_id: r.family_id,
    revision_key: typeof r.revision_key === 'string' ? r.revision_key : null,
    revision_source: toRevisionSource(r.revision_source),
  }
}

/**
 * Family-level lifecycle status (`RegistryFamily.family_status`, backend
 * `FamilyStatus` StrEnum: active/superseded/repealed/merged + `_missing_` ->
 * unknown). `withdrawn` is accepted defensively too — the spec's prose model
 * (§3) lists it at family level even though today's backend enum doesn't;
 * tolerating it costs nothing and avoids a future silent `unknown` if it is
 * ever added.
 */
export type FamilyStatus = 'active' | 'superseded' | 'withdrawn' | 'repealed' | 'merged' | 'unknown'
const FAMILY_STATUSES: ReadonlySet<string> = new Set([
  'active',
  'superseded',
  'withdrawn',
  'repealed',
  'merged',
])
export function toFamilyStatus(v: unknown): FamilyStatus {
  return typeof v === 'string' && FAMILY_STATUSES.has(v) ? (v as FamilyStatus) : 'unknown'
}

/** Mirrors CMS `LocalizedText` — both locales optional, tolerant of either
 *  being absent (never inferred/fabricated). */
export interface LocalizedText {
  en: string | null
  fr: string | null
}

export function parseLocalizedText(raw: unknown): LocalizedText {
  // Same parse-time union as the CMS LocalizedText (models/i18n.py): a bare
  // string is the "en" entry. Most registry family titles are stored that
  // way, and dropping them hid the family title everywhere (MYAC-1463).
  if (typeof raw === 'string') return { en: raw || null, fr: null }
  if (!raw || typeof raw !== 'object') return { en: null, fr: null }
  const r = raw as Record<string, unknown>
  return {
    en: typeof r.en === 'string' && r.en ? r.en : null,
    fr: typeof r.fr === 'string' && r.fr ? r.fr : null,
  }
}

/** Locale-aware pick, EN falling back to FR and vice versa; null when
 *  neither locale has a value. */
export function pickLocalizedText(text: LocalizedText | null, locale: string): string | null {
  if (!text) return null
  const primary = locale.startsWith('fr') ? text.fr : text.en
  const secondary = locale.startsWith('fr') ? text.en : text.fr
  return primary ?? secondary ?? null
}

/** A "did you mean X?" near-miss candidate — either an EXISTING family
 *  (`family_id` set) or a plain display string the curator found evidence
 *  for but never minted. Mirrors CMS `UnverifiableSuggestion`. */
export interface UnverifiableSuggestion {
  family_id: string | null
  display_ref: string
}

export function parseUnverifiableSuggestion(raw: unknown): UnverifiableSuggestion | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  if (typeof r.display_ref !== 'string' || !r.display_ref) return null
  return {
    family_id: typeof r.family_id === 'string' && r.family_id ? r.family_id : null,
    display_ref: r.display_ref,
  }
}

function parseUnverifiableSuggestions(raw: unknown): UnverifiableSuggestion[] {
  if (!Array.isArray(raw)) return []
  return raw.map(parseUnverifiableSuggestion).filter((s): s is UnverifiableSuggestion => s !== null)
}

/** Minimal client-visible projection of one `RegistryFamily.revisions[key]`
 *  entry (backend `Revision`, `models/registry.py`) — the subset the §8 chip
 *  derivation + enriched norms columns need: status, dates, revision_label,
 *  successor, plus `source_links` (MYAC-904 P4.3 D6): the norms/report
 *  surfaces need a SUCCESSOR revision's own links (rendered on a superseded
 *  card ahead of the subscribed revision's links), and the successor is
 *  resolved live from this SAME family projection — there's no other read
 *  path to its links. Still excludes purely curator-internal fields
 *  (`adoption_labels`, ...) not needed by any client surface. */
export interface RegistryRevisionProjection {
  key: string
  status: RevisionStatus
  dates: RevisionDates
  revision_label: string | null
  successor: RegistryLink | null
  /** Optional in fixtures; the parser always sets it. */
  successor_relation?: SuccessorRelation
  harmonized: boolean | null
  source_links: SourceLink[]
  /** Alias revision-key spellings that denote THIS edition (year-drift across
   *  adoption layers, reaffirmation spellings, short forms — MYAC-904). The
   *  resolver lane and the report pipeline already match through these; the
   *  norms view must too, or a server-side alias repair never reaches the UI
   *  (observed 2026-07-31: rows pinning '2005' stayed "awaiting registry
   *  verification" after the registry aliased 2005 onto the 2004 edition). */
  identifiers: string[]
}

function parseRevisionProjection(key: string, raw: unknown): RegistryRevisionProjection {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const sourceLinksRaw = Array.isArray(r.source_links) ? r.source_links : []
  return {
    key,
    status: toRevisionStatus(r.status),
    dates: parseRevisionDates(r.dates),
    revision_label:
      typeof r.revision_label === 'string' && r.revision_label
        ? r.revision_label
        : typeof r.edition_label === 'string' && r.edition_label
          ? r.edition_label // tolerant reader: pre-rename field name (§18)
          : null,
    successor: parseRegistryLink(r.successor),
    successor_relation: toSuccessorRelation(r.successor_relation),
    harmonized: typeof r.harmonized === 'boolean' ? r.harmonized : null,
    source_links: sourceLinksRaw.map(parseSourceLink).filter((s): s is SourceLink => s !== null),
    identifiers: Array.isArray(r.identifiers)
      ? r.identifiers.filter((v): v is string => typeof v === 'string')
      : [],
  }
}

function parseRevisionsMap(raw: unknown): Record<string, RegistryRevisionProjection> {
  if (!raw || typeof raw !== 'object') return {}
  const out: Record<string, RegistryRevisionProjection> = {}
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    out[key] = parseRevisionProjection(key, value)
  }
  return out
}

/**
 * Client-visible projection of a `registry_families/{family_id}` doc — the
 * subset the front's §8 chip derivation and enriched norms table need.
 * Deliberately NOT a full mirror of `RegistryFamily`: server-only workflow
 * fields never appear here (they live on `registry_families_state`, which
 * the front can never read — see firestore.rules).
 *
 * Tolerant parsing throughout: every field degrades to a safe default
 * (`'unknown'`, `null`, `[]`) rather than throwing, per the StrEnum/model
 * resilience rules (CLAUDE.md).
 */
export interface RegistryFamilyProjection {
  family_id: string
  display_ref: string
  title: LocalizedText
  authority: string
  confidence_tier: ConfidenceTier
  family_status: FamilyStatus
  latest_revision: string | null
  revisions: Record<string, RegistryRevisionProjection>
  identifiers: string[]
  last_verified: Date | null
  /** Convenience mirror of the CURRENT revision's `harmonized` flag (the
   *  fact itself lives per-revision on `revisions[key].harmonized`) — hoisted
   *  here so table columns don't re-index into `revisions` on every render.
   *  "Current" = `latest_revision`'s entry when present, else undefined. */
  harmonized: boolean | null
  unverifiable_suggestions: UnverifiableSuggestion[]
  merged_into: string | null
  /** Demand lifecycle (MYAC-904, 2026-08-11): revision keys a curator
   *  RESEARCHED and REJECTED as nonexistent editions, keyed to the
   *  researched note. Lets a dangling pin say "registry checked: no such
   *  edition" instead of the eternal "awaiting registry verification".
   *  Empty for families written before the field existed. Optional in the
   *  type (the parser always sets it) so pre-existing fixtures stay valid. */
  rejected_identifiers?: Record<string, string>
  /** Identity-level rejection (MYAC-904, 2026-08-12): the terminal answer
   *  that this family's whole DESIGNATION is not a trackable instrument
   *  (umbrella framework / verified nonexistent / irreducibly ambiguous),
   *  with the curator's researched note and "follow X instead" suggestions.
   *  `null` for every normal family. */
  rejection?: IdentityRejection | null
}

export type IdentityRejectionKind = 'umbrella' | 'not_an_instrument' | 'ambiguous' | 'unknown'

export interface IdentityRejection {
  kind: IdentityRejectionKind
  note: string
  suggestions: UnverifiableSuggestion[]
}

export function parseIdentityRejection(raw: unknown): IdentityRejection | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const kind: IdentityRejectionKind =
    r.kind === 'umbrella' || r.kind === 'not_an_instrument' || r.kind === 'ambiguous'
      ? r.kind
      : 'unknown'
  return {
    kind,
    note: typeof r.note === 'string' ? r.note : '',
    suggestions: Array.isArray(r.suggestions)
      ? r.suggestions.map(parseUnverifiableSuggestion).filter((s): s is UnverifiableSuggestion => s !== null)
      : [],
  }
}

export function parseRegistryFamilyProjection(id: string, raw: unknown): RegistryFamilyProjection | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const revisions = parseRevisionsMap(r.revisions ?? r.editions)
  const latestRevision =
    typeof r.latest_revision === 'string' && r.latest_revision
      ? r.latest_revision
      : typeof r.latest_edition === 'string' && r.latest_edition
        ? r.latest_edition
        : null
  return {
    family_id: typeof r.family_id === 'string' && r.family_id ? r.family_id : id,
    display_ref: typeof r.display_ref === 'string' ? r.display_ref : '',
    title: parseLocalizedText(r.title),
    authority: typeof r.authority === 'string' ? r.authority : '',
    confidence_tier: toConfidenceTier(r.confidence_tier),
    family_status: toFamilyStatus(r.family_status),
    latest_revision: latestRevision,
    revisions,
    identifiers: Array.isArray(r.identifiers) ? r.identifiers.filter((v): v is string => typeof v === 'string') : [],
    last_verified: toDateOrNull(r.last_verified),
    harmonized: latestRevision && revisions[latestRevision] ? revisions[latestRevision].harmonized : null,
    unverifiable_suggestions: parseUnverifiableSuggestions(r.unverifiable_suggestions),
    merged_into: typeof r.merged_into === 'string' && r.merged_into ? r.merged_into : null,
    rejected_identifiers: parseRejectedIdentifiers(r.rejected_identifiers),
    rejection: parseIdentityRejection(r.rejection),
  }
}

function parseRejectedIdentifiers(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== 'object') return {}
  const out: Record<string, string> = {}
  for (const [key, note] of Object.entries(raw as Record<string, unknown>)) {
    if (key && typeof note === 'string') out[key] = note
  }
  return out
}

/** `family_id` grammar test (mint() = `slug(authority) + "__" + slug(base_ref)`,
 *  always double-underscore; `alias_key()` never produces one). Used to tell
 *  apart a genuine (but currently unresolved/non-exposable) family reference
 *  from a `registry_ref.family_id` that is actually still an alias-key
 *  quarantine stamp (`registry_unresolved`, resolve lane, Stage-A of the
 *  lifecycle) — see `src/utils/regulationResolutionState.ts` row 5 vs. the
 *  "being resolved" state. */
export function looksLikeMintedFamilyId(familyId: string): boolean {
  return familyId.includes('__')
}
